/**
 * Generic metadata decorator system with class-based architecture
 */

// ===== Base Generic Decorator Singleton Class =====

// Generic metadata type that includes original method
export type DecoMetadata<T> = {
  value: T;
  originalMethod?: Function;
  methodName: string;
};

abstract class DecoBase<T> {
  
  // Static singleton extention pattern
  protected static instances: Map<any, any> = new Map(); 
  
  public static getInstance<T extends DecoBase<any>>(this: new () => T): T {
        if (!DecoBase.instances.has(this)) {
            DecoBase.instances.set(this, new this());
        }
        return DecoBase.instances.get(this);
  }

  // Instance level
  protected metadataKey: symbol;
  
  protected constructor() {
    // Use the child class name as the symbol
    this.metadataKey = Symbol(this.constructor.name);
  }

  // Decorator function with automatic original method preservation
  decorator(value?: T) {
    const metadataKey = this.metadataKey;
    return function(target: any, propertyKey: string, descriptor: PropertyDescriptor): PropertyDescriptor {
      // Get or create the metadata storage on the constructor
      if (!target.constructor[metadataKey]) {
        target.constructor[metadataKey] = new Map<string, DecoMetadata<T>>();
      }
      
      // Store metadata with original method
      target.constructor[metadataKey].set(propertyKey, {
        value: value as T,
        originalMethod: descriptor?.value,
        methodName: propertyKey
      });
      
      return descriptor;
    };
  }
  
  // Get full metadata including original method
  getMetadata(instance: any, methodName: string): DecoMetadata<T> | undefined {
    const metadataMap = instance.constructor[this.metadataKey];
    return metadataMap?.get(methodName);
  }
  
  // Get all method names with visibility check support
  getAll(instance: any, display: boolean = true): string[] {
    const keys = this.getKeys(instance);
    
    if (!display && instance instanceof HTMLElement) {
      if (instance.style.getPropertyValue("display") === "none") {
        return [];
      }
    }    
    return keys;
  }
  
  // Get all method names that have this metadata
  getKeys(instance: any): string[] {
    const metadataMap = instance.constructor[this.metadataKey];
    return metadataMap ? Array.from(metadataMap.keys()) : [];
  }
  
  // Get all full metadata including original methods
  getAllFullMetadata(instance: any): Map<string, DecoMetadata<T>> {
    return instance.constructor[this.metadataKey] || new Map();
  }
}


// ===== Event Binding Decorator System =====
type EventMetadata = {
  eventType: string;
  querySelector: string;
};

export class EventDecorator extends DecoBase<EventMetadata> {
   constructor() {
    super();
  }
  
  // Setup event listeners on an instance (call this in constructor/connectedCallback)
  setupEvents(instance: any) {
    const metadataMap = this.getAllFullMetadata(instance);
    
    metadataMap.forEach((metadata, methodName) => {
      if (!metadata.value || !metadata.originalMethod) return;
      
      const { eventType, querySelector } = metadata.value;
      const element = instance.querySelector(querySelector);
        
      if (element) {
        // Bind method to instance context
        element.addEventListener(eventType, metadata.originalMethod.bind(instance));
        console.log(`${eventType} ${querySelector} ${methodName} `)
      } else {
        console.warn(`Element not found for event binding: ${querySelector}`);
      }
    });
  }
}

// Export event decorator
export function event(eventType: string, querySelector: string) {
  return EventDecorator.getInstance().decorator({ eventType, querySelector });
}

// ===== Action Deco =====
export class ActionDecorator extends DecoBase<boolean> {
   constructor() {
    super();
  }
}

export function action() {
  return ActionDecorator.getInstance().decorator(true);
}

// ===== Action Deco =====
export class DefaultActionDecorator extends DecoBase<boolean> {
   constructor() {
    super();
  }
}

export function defaultAction() {
  return DefaultActionDecorator.getInstance().decorator(true);
}

// ===== Keywords Deco  =====
export class KeywordsDecorator extends DecoBase<string[]> {
   constructor() {
    super();
  }
}

export function keywords(...words: string[]) {
  return KeywordsDecorator.getInstance().decorator(words);
}
